import {
  faDeleteRight,
  faPenLine,
  faSquareList,
  faTrashCan,
} from "@fortawesome/pro-duotone-svg-icons";
import { batch } from "@preact/signals-react";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import { ulid } from "ulid";
import { ColorChangingIcon } from "../Common/UI/ColorChangingIcon";
import { FieldTypes } from "../Fields/FieldTypes/Fields";
import { fieldPropertyMap } from "../Fields/PropertyTypes/FieldPropertyMap";
import math from "../Mathjs/CustomMathjsInstance";
import {
  editSection,
  editSectionIndex,
  fieldFocusIndex,
  propertiesVisible,
  sectionFocusIndex,
  templateData,
} from "./NewTemplateSignals";

const SectionPanelBar = () => {
  //#region Field Operations
  function addField(sectionIndex) {
    const sections = [...templateData.value.sections];
    const section = { ...sections[sectionIndex] };
    const newField = {
      fieldId: ulid(),
      fieldName: "",
      fieldType: "",
      properties: { newField: true },
    };
    section.fields = [...section.fields, newField];
    sections[sectionIndex] = section;
    templateData.value = { ...templateData.value, sections: sections };
  }

  function removeField(sectionIndex, fieldIndex) {
    const sections = [...templateData.value.sections];
    const updatedSections = sections.map((section, index) => {
      if (index === sectionIndex) {
        return {
          ...section,
          fields: section.fields.filter((_, fIndx) => fIndx !== fieldIndex),
        };
      }
      return section;
    });

    templateData.value = { ...templateData.value, sections: updatedSections };
  }

  function validateFieldName(value) {
    const firstChar = value?.charAt(0);

    if (!firstChar) {
      return { isValid: true, errorMessage: "" };
    }

    if (/\d/.test(firstChar)) {
      return {
        isValid: false,
        errorMessage: "Field names cannot start with numbers.",
      };
    }

    if (/[!@#$%^&*()_+\-=\]{};':"\\|,.<>?]/.test(firstChar)) {
      return {
        isValid: false,
        errorMessage: "Field names cannot start with special symbols.",
      };
    }

    if (Object.hasOwn(math, value)) {
      return {
        isValid: false,
        errorMessage: `Field name "${value}" conflicts with an existing math function.`,
      };
    }

    return { isValid: true, errorMessage: "" };
  }
  //#endregion Field Operations

  //#region Section Operations
  function handleSectionFocus(sectionIndex, fieldIndex) {
    sectionFocusIndex.value = sectionIndex;
    fieldFocusIndex.value = fieldIndex;
  }

  function removeSection(sectionIndex) {
    const sections = [...templateData.value.sections];
    const updatedSections = [...sections];
    updatedSections.splice(sectionIndex, 1);
    templateData.value = { ...templateData.value, sections: updatedSections };
  }

  function startEditing(e, index) {
    e.stopPropagation();
    editSectionIndex.value = index;
    sectionFocusIndex.value = index;
    editSection.value = true;
  }

  function stopEditing(e, index) {
    e.stopPropagation();
    handleSectionNameChange(e, index);
    editSection.value = false;
  }

  function handleSectionNameChange(e, sectionIndex) {
    const sections = [...templateData.value.sections];
    const updatedSection = { ...sections[sectionIndex] };
    updatedSection.sectionName = e.target.value;
    sections[sectionIndex] = updatedSection;
    templateData.value = { ...templateData.value, sections: sections };
  }

  const handleFieldTypeChange = (field, newFieldType) => {
    const validPropertiesForType = fieldPropertyMap[newFieldType]?.template || [];
    const validPropertyNames = validPropertiesForType.map((prop) => prop.propertyName);

    if (validPropertyNames.length === 0) {
      return { ...field };
    }

    const filteredProperties = {};
    Object.keys(field.properties || {}).forEach((propName) => {
      if (validPropertyNames.includes(propName)) {
        filteredProperties[propName] = field.properties[propName];
      }
    });

    return {
      ...field,
      properties: filteredProperties,
    };
  };

  //#endregion Section Operations

  return (
    <PanelBar
      expandMode="single"
      onSelect={(e) => {
        batch(() => {
          sectionFocusIndex.value = e.target.props?.sectionIndex;
          fieldFocusIndex.value = null;
        });
      }}
    >
      {templateData.value?.sections?.map((section, sectionIndex) => (
        <PanelBarItem
          sectionIndex={sectionIndex}
          title={
            <div
              className="k-d-flex k-w-full"
              name={"divTemplateSectionHeader_" + sectionIndex}
            >
              <div className="k-d-flex k-w-full">
                <ColorChangingIcon
                  title="Remove Section"
                  icon={faTrashCan}
                  initialPrimaryColor="#6e6e6e"
                  initialSecondaryColor="#919191"
                  initialSize={18}
                  hoverSize={18}
                  padding="0 4px 0 0"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSection(sectionIndex);
                  }}
                />
                {!editSection.value && (
                  <ColorChangingIcon
                    title="Edit Section Name"
                    icon={faPenLine}
                    initialPrimaryColor="#F4A427"
                    initialSecondaryColor="#d6562a"
                    initialSize={18}
                    hoverSize={18}
                    padding="0 4px 0 4px"
                    onClick={(e) => {
                      e.stopPropagation();
                      batch(() => {
                        sectionFocusIndex.value = null;
                      });
                      batch(() => {
                        propertiesVisible.value = true;
                        sectionFocusIndex.value = sectionIndex;
                        fieldFocusIndex.value = null;
                      });
                      startEditing(e, sectionIndex);
                    }}
                  />
                )}
                {editSection.value && (
                  <ColorChangingIcon
                    title="Save Section Name"
                    icon={faPenLine}
                    initialPrimaryColor="#F4A427"
                    initialSecondaryColor="#d6562a"
                    initialSize={18}
                    hoverSize={18}
                    padding="0 4px 0 4px"
                    onClick={(e) => {
                      e.stopPropagation();
                      batch(() => {
                        sectionFocusIndex.value = null;
                      });
                      batch(() => {
                        propertiesVisible.value = true;
                        sectionFocusIndex.value = sectionIndex;
                        fieldFocusIndex.value = null;
                        editSection.value = !editSection.value;
                      });
                    }}
                  />
                )}
                {editSectionIndex.value === sectionIndex && editSection.value ? (
                  <input
                    type="text"
                    defaultValue={section.sectionName}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => stopEditing(e, sectionFocusIndex.value)}
                  />
                ) : (
                  <span onClick={(e) => startEditing(e, sectionIndex)}>{section.sectionName}</span>
                )}
              </div>
              <div className="k-align-self-end">
                <ColorChangingIcon
                  title="Properties"
                  icon={faSquareList}
                  initialPrimaryColor="#a6e6ff"
                  initialSecondaryColor="#0078d4"
                  padding="0 4px 0 4px"
                  float="right"
                  onClick={(e) => {
                    e.stopPropagation();
                    batch(() => {
                      sectionFocusIndex.value = null;
                    });
                    batch(() => {
                      propertiesVisible.value = true;
                      sectionFocusIndex.value = sectionIndex;
                      fieldFocusIndex.value = null;
                    });
                  }}
                />
              </div>
            </div>
          }
          key={section.id}
          expanded={sectionIndex === sectionFocusIndex.value}
          headerClassName="k-border k-border-black k-border-solid k-w-full k-d-flex !k-text-white !k-bg-primary"
        >
          <div className="k-d-flex k-flex-col k-var--neutral-30">
            <div className="k-my-4">
              <Button
                type="button"
                name="btnTemplateAddField"
                themeColor={"primary"}
                onClick={() => {
                  sectionFocusIndex.value = sectionIndex;
                  addField(sectionIndex);
                }}
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base k-ml-2.5"
              >
                Add Field
              </Button>
            </div>
            {section.fields?.map((field, fieldIndex) => (
              <div key={`${sectionIndex}-field-${fieldIndex}`}>
                <div className="align-columns">
                  <ColorChangingIcon
                    title="Remove Field"
                    name={"btnTemplateRemoveField_s" + sectionIndex + "_f" + fieldIndex}
                    icon={faDeleteRight}
                    initialPrimaryColor="#a6e6ff"
                    initialSecondaryColor="#0078d4"
                    margin="17px 0 0 10px"
                    onClick={() => removeField(sectionIndex, fieldIndex)}
                  />
                  <div style={{ width: "30%", position: "relative" }}>
                    <Input
                      className={
                        templateData.value?.sections?.[sectionIndex]?.fields?.[fieldIndex]
                          .fieldName && validateFieldName(field.fieldName).isValid
                          ? "!k-valid"
                          : "!k-invalid"
                      }
                      style={{
                        width: "100%",
                        color:
                          templateData.value.properties.TemplateSync &&
                          !templateData.value.properties?.AllowFieldNameEdit &&
                          !field.properties?.newField
                            ? "gray"
                            : "",
                      }}
                      name={"txtTemplateFieldName_s" + sectionIndex + "_f" + fieldIndex}
                      label={"Field Name"}
                      required={true}
                      disabled={
                        templateData.value.properties?.TemplateSync &&
                        !templateData.value.properties?.AllowFieldNameEdit &&
                        !field.properties?.newField
                      }
                      value={field.fieldName}
                      onChange={(e) => {
                        const value = e.target.value;
                        const sections = [...templateData.value.sections];
                        const updatedSection = { ...sections[sectionIndex] };
                        const updatedFields = updatedSection.fields.map((f, fIndex) => {
                          if (fIndex === fieldIndex) {
                            return { ...f, fieldName: value };
                          }
                          return f;
                        });
                        updatedSection.fields = updatedFields;
                        sections[sectionIndex] = updatedSection;
                        templateData.value = { ...templateData.value, sections: sections };
                      }}
                      onFocus={() => {
                        sectionFocusIndex.value = null;
                        batch(() => {
                          sectionFocusIndex.value = sectionIndex;
                          fieldFocusIndex.value = fieldIndex;
                        });
                      }}
                    />
                    {field.fieldName && !validateFieldName(field.fieldName).isValid && (
                      <>
                        <div className="!k-d-flex k-font-size-sm !k-font-weight-semibold !k-text-error">
                          {validateFieldName(field.fieldName).errorMessage}
                        </div>
                        <div className="!k-d-flex k-font-size-sm !k-font-weight-semibold !k-text-info">
                          The &apos;Label Text&apos; property lets you show any name, even if the
                          field name has restrictions.
                        </div>
                      </>
                    )}
                  </div>
                  <DropDownList
                    className={
                      templateData.value?.sections?.[sectionIndex]?.fields?.[fieldIndex].fieldType
                        ? "!k-valid"
                        : "!k-invalid"
                    }
                    style={{ width: "30%" }}
                    name={"ddlTemplateFieldType_s" + sectionIndex + "_f" + fieldIndex}
                    label={"Field Type"}
                    required={true}
                    data={FieldTypes.filter((f) => f !== "TableIndex")}
                    value={field.fieldType}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sections = [...templateData.value.sections];
                      const updatedSection = { ...sections[sectionIndex] };
                      const updatedFields = updatedSection.fields.map((f, fIndex) => {
                        if (fIndex === fieldIndex) {
                          const updatedField = handleFieldTypeChange(f, value);
                          return { ...updatedField, fieldType: value };
                        }
                        return f;
                      });
                      updatedSection.fields = updatedFields;
                      sections[sectionIndex] = updatedSection;
                      templateData.value = { ...templateData.value, sections: sections };
                      handleSectionFocus(sectionIndex, fieldIndex);
                    }}
                    onClick={() => {
                      sectionFocusIndex.value = null;
                      batch(() => {
                        sectionFocusIndex.value = sectionIndex;
                        fieldFocusIndex.value = fieldIndex;
                      });
                    }}
                    onFocus={() => {
                      sectionFocusIndex.value = null;
                      batch(() => {
                        sectionFocusIndex.value = sectionIndex;
                        fieldFocusIndex.value = fieldIndex;
                      });
                    }}
                  />

                  <ColorChangingIcon
                    title="Properties"
                    icon={faSquareList}
                    initialPrimaryColor="#a6e6ff"
                    initialSecondaryColor="#0078d4"
                    margin="17px 0 0 10px"
                    onClick={() => {
                      sectionFocusIndex.value = null;
                      batch(() => {
                        propertiesVisible.value = true;
                        sectionFocusIndex.value = sectionIndex;
                        fieldFocusIndex.value = fieldIndex;
                      });
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </PanelBarItem>
      ))}
    </PanelBar>
  );
};
export default SectionPanelBar;
